import React, { useState } from "react";
import { X, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { useT } from "../context/LanguageContext";

const initial = { name: "", email: "", phone: "" };

// Must match the backend's format (api/routes/public_marketing.py _PHONE_RE):
// optional leading "+", then 7-15 digits, no spaces/dashes/parens.
const PHONE_RE = /^\+?[1-9]\d{6,14}$/;
const normalizePhone = (value) => value.replace(/[\s()-]/g, "");

/**
 * Reusable name/email/phone capture form. Rendered inline (no modal chrome)
 * so it can be embedded either standalone (LeadCaptureModal, below) or as a
 * step inside a larger flow (see AgentAccessModal).
 */
export const LeadForm = ({
  onSubmit,
  submitting = false,
  error = "",
  submitLabel,
  onBack,
}) => {
  const t = useT();
  const [form, setForm] = useState(initial);
  const [phoneError, setPhoneError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const phone = normalizePhone(form.phone);
    if (!PHONE_RE.test(phone)) {
      setPhoneError(t("agentAccess.lead.phoneError"));
      return;
    }
    setPhoneError("");
    onSubmit({ ...form, phone });
  };


  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="form-label">{t("agentAccess.lead.name")} *</label>
          <input
            className="form-input"
            name="name"
            required
            value={form.name}
            onChange={handleChange}
          />
        </div>
        <div>
          <label className="form-label">{t("agentAccess.lead.email")} *</label>
          <input
            className="form-input"
            type="email"
            name="email"
            required
            value={form.email}
            onChange={handleChange}
          />
        </div>
        <div>
          <label className="form-label">{t("agentAccess.lead.phone")} *</label>
          <input
            className="form-input"
            name="phone"
            required
            placeholder="+90 5XX XXX XX XX"
            value={form.phone}
            onChange={handleChange}
          />
          {phoneError && (
            <p className="mt-1.5 text-[12.5px]" style={{ color: "var(--brand-coral)" }}>
              {phoneError}
            </p>
          )}
        </div>
      </div>

      {error && (
        <p
          className="mt-4 text-[13px] px-3 py-2 rounded-lg"
          style={{ background: "rgba(242, 92, 102, 0.10)", color: "var(--brand-coral)" }}
        >
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3 text-[14px] font-semibold"
            style={{ border: "1px solid var(--border)", color: "var(--fg)" }}
          >
            <ArrowLeft size={16} /> {t("agentAccess.lead.back")}
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold neon-btn disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> {t("agentAccess.lead.submitting")}
            </>
          ) : (
            <>
              {submitLabel || t("agentAccess.lead.submit")} <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/**
 * Standalone modal wrapper around LeadForm — used by ChatWidgetButton to
 * collect name/email/phone before starting a chat session.
 */
const LeadCaptureModal = ({ open, onClose, onSubmit, submitting = false, error = "" }) => {
  const t = useT();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(15, 21, 48, 0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:opacity-70"
          style={{ color: "var(--fg)" }}
          aria-label={t("agentAccess.calling.close")}
        >
          <X size={18} />
        </button>

        <div className="px-7 pt-7 pb-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <h3 className="text-[20px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
            {t("agentAccess.lead.title")}
          </h3>
          <p className="mt-1.5 text-[13.5px]" style={{ color: "var(--muted)" }}>
            {t("agentAccess.lead.subtitle")}
          </p>
        </div>

        <div className="px-7 py-6">
          <LeadForm onSubmit={onSubmit} submitting={submitting} error={error} />
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
